import { getTxnInfo, markAsSkip } from '@api/form-verification';
import { handleFetchConfigs } from '@containers/form-verification/helpers';
import {
  growthBookProfileVerificationsFeature,
  ProfileVerifications
} from '@global/constants/verification';
import { isClientCareerMosaic } from '@global/helpers/general';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { Box, Button, Dialog, Typography } from '@mui/material';
import { RootState } from '@redux/store';
import { growthbook } from '@services/growthbook';
import { navigateTo } from '@services/navigation';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import styles from './styles';
import { LeftVerifications } from './types';

export default function PendingVerifications({
  verificationsLeft,
  setVerificationsLeft
}: {
  verificationsLeft: LeftVerifications,
  setVerificationsLeft: React.Dispatch<React.SetStateAction<LeftVerifications>>
}) {
  const verificationsForProfile = useFeatureValue(
    growthBookProfileVerificationsFeature,
    ProfileVerifications.PASSPORT
  );

  const formData = useSelector((s: RootState) => s.formData);
  const { orgInfo, txnId } = formData ?? {};
  const orgId = orgInfo?.orgId;
  const verification = useSelector((s: RootState) => s.verification);
  const { configs } = verification ?? {};
  const { atleastMapping, skippables } = verificationsLeft ?? {};

  const handleClose = () => {
    setVerificationsLeft(null);
  };

  const handleSkipAll = async () => {
    const res = await markAsSkip({
      orgId,
      txnId,
      verificationNameList: verificationsLeft?.list ?? []
    });
    if (res?.code === 0) {
      const res = await getTxnInfo(txnId, orgId);
      await handleFetchConfigs(res?.data, verificationsForProfile);
      handleClose();
    }
  };

  useEffect(() => {
    growthbook.loadFeatures();
  }, []);

  return (
    <Dialog
      maxWidth="xs"
      fullWidth
      open={Boolean(verificationsLeft)}
      onClose={handleClose}
      sx={{ minWidth: 0 }}>
      {verificationsLeft?.list && (
        <Box sx={styles.pending.topRow}>
          <Typography variant="body1" sx={{ fontWeight: '600' }}>
            {`Please complete ${!skippables ? '' : 'or skip'} these verifications`}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: '400' }}>
            {!skippables
              ? 'You need to complete the mandatory verifications'
              : 'You need to either complete or skip the remaining verifications'}
          </Typography>
        </Box>
      )}
      <Box sx={{ padding: '1.8em', width: '100%', maxHeight: '60vh', overflowY: 'auto' }}>
        <>
          {verificationsLeft?.list && (
            <Box sx={{ display: 'flex', flexFlow: 'column', rowGap: '2.1rem' }}>
              {verificationsLeft?.list?.map((item, index) => (
                <Typography
                  variant="body1"
                  key={item}
                  sx={{
                    color: 'font.medium',
                    fontWeight: '500'
                  }}>{`${index + 1}. ${configs?.[item]?.metadata?.label}`}</Typography>
              ))}
            </Box>
          )}
          {atleastMapping && (
            <>
              {Object.keys(atleastMapping ?? {})?.map((key: string) => (
                <React.Fragment key={key}>
                  <Typography sx={{ fontSize: '1.6rem', fontWeight: '700', mb: '1.8rem' }}>
                    Complete atleast {verificationsLeft?.atleastMapping?.[key]} from these:
                  </Typography>
                  <Box sx={{ display: 'flex', flexFlow: 'column', rowGap: '1rem' }}>
                    {key.split(',')?.map(
                      (verificationName, index) =>
                        configs?.[verificationName] && (
                          <Typography variant="body1" key={verificationName}>
                            {`${index + 1}. ${configs?.[verificationName]?.metadata?.label}`}
                          </Typography>
                        )
                    )}
                  </Box>
                </React.Fragment>
              ))}
            </>
          )}
        </>
      </Box>
      <Box
        sx={{
          ...styles.pending.bottomRow
        }}>
        {!skippables && (
          <Button
            sx={{
              typography: 'body1',
              borderRadius: '0',
              p: '.5rem 1rem',
              color: 'primary.main',
              width: 'fit-content'
            }}
            onClick={handleClose}>
            Cancel
          </Button>
        )}
        {verificationsLeft?.list && (
          <Button
            sx={{
              typography: 'body1',
              borderRadius: '4px',
              p: '0.5rem 1rem',
              color: 'primary.main',
              width: 'fit-content'
            }}
            onClick={() => {
              if (isClientCareerMosaic()) {
                handleClose();
              } else {
                navigateTo('/', { replace: true });
              }
            }}>
            Complete later
          </Button>
        )}
        {skippables && (
          <Button
            sx={{
              typography: 'body1',
              borderRadius: '0',
              p: '.5rem 1rem',
              color: 'primary.main',
              width: 'fit-content'
            }}
            onClick={handleSkipAll}>
            Skip pending
          </Button>
        )}
      </Box>
    </Dialog>
  );
}
